import React, { useState } from "react";
import MetaTags from "react-meta-tags";
import { Link, useHistory } from "react-router-dom";
import { Alert, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import AuthLayout from "../../components/authenticate/AuthLayout";

const ChangePasswordPage = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState(null);
  const [sameAsTempError, setSameAsTempError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const history = useHistory();
  const token = localStorage.getItem("token");

  // Live password validation rules
  const ruleMinLength = newPassword.length >= 8;
  const ruleMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const ruleDifferent = !sameAsTempError && ruleMinLength;
  const isValid = ruleMinLength && ruleMatch && !sameAsTempError;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSameAsTempError(false);

    if (!ruleMinLength) {
      setError("New password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          newPassword,
        }),
      });

      const data = await resp.json();
      if (resp.ok && data.status) {
        setSuccess(true);
        localStorage.setItem("mustChangePassword", "false");
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 1200);
      } else {
        const errMsg = data.error || "Failed to update password";
        if (errMsg.toLowerCase().includes("different from the temporary")) {
          setSameAsTempError(true);
        }
        setError(errMsg);
      }
    } catch (err) {
      setError(err.message || "Network error updating password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <MetaTags>
        <title>Set New Password | Glogix</title>
      </MetaTags>

      <AuthLayout
        footer={
          <>
            Signed in with temporary credentials · <Link to="/logout">Logout</Link>
          </>
        }
      >
        <div className="gx-auth-card">
          <div className="gx-auth-title">
            <h4>Set your new password</h4>
            <p>Your admin created this account with a temporary password. For your security, choose a new password to continue.</p>
          </div>

          {error && (
            <Alert color="danger" className="d-flex align-items-center py-2 mb-3">
              <FeatherIcon icon="alert-circle" size={16} className="me-2 flex-shrink-0" />
              <span>{error}</span>
            </Alert>
          )}

          {success && (
            <Alert color="success" className="d-flex align-items-center py-2 mb-3">
              <FeatherIcon icon="check-circle" size={16} className="me-2 flex-shrink-0" />
              <span>Password updated successfully! Redirecting to dashboard...</span>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="custom-form">
            {/* New Password */}
            <div className="mb-3">
              <label className="form-label text-white">New Password</label>
              <div className="gx-password">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="Enter new password (min. 8 characters)"
                  value={newPassword}
                  required
                  autoFocus
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (sameAsTempError) setSameAsTempError(false);
                    if (error) setError(null);
                  }}
                />
                <button
                  type="button"
                  className="gx-password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  <FeatherIcon icon={showNewPassword ? "eye-off" : "eye"} size={16} />
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div className="mb-3">
              <label className="form-label text-white">Confirm New Password</label>
              <div className="gx-password">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  required
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError(null);
                  }}
                />
                <button
                  type="button"
                  className="gx-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  <FeatherIcon icon={showConfirmPassword ? "eye-off" : "eye"} size={16} />
                </button>
              </div>
            </div>

            {/* Live Password Rules Checklist */}
            <div
              className="password-rules-checklist mb-4"
              style={{
                background: "rgba(15, 23, 42, 0.45)",
                borderRadius: "10px",
                padding: "14px 16px",
                border: "1px solid rgba(148, 163, 184, 0.12)",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  color: "rgba(255, 255, 255, 0.6)",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                }}
              >
                Password Requirements
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    color: ruleMinLength ? "#10b981" : "rgba(255, 255, 255, 0.5)",
                    transition: "color 0.2s ease",
                  }}
                >
                  <FeatherIcon
                    icon={ruleMinLength ? "check-circle" : "circle"}
                    size={14}
                    style={{ color: ruleMinLength ? "#10b981" : "rgba(255, 255, 255, 0.3)" }}
                  />
                  <span>At least 8 characters</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    color: ruleDifferent ? "#10b981" : sameAsTempError ? "#ef4444" : "rgba(255, 255, 255, 0.5)",
                    transition: "color 0.2s ease",
                  }}
                >
                  <FeatherIcon
                    icon={ruleDifferent ? "check-circle" : sameAsTempError ? "x-circle" : "circle"}
                    size={14}
                    style={{
                      color: ruleDifferent
                        ? "#10b981"
                        : sameAsTempError
                        ? "#ef4444"
                        : "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                  <span>Different from temporary password</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    color: ruleMatch ? "#10b981" : "rgba(255, 255, 255, 0.5)",
                    transition: "color 0.2s ease",
                  }}
                >
                  <FeatherIcon
                    icon={ruleMatch ? "check-circle" : "circle"}
                    size={14}
                    style={{ color: ruleMatch ? "#10b981" : "rgba(255, 255, 255, 0.3)" }}
                  />
                  <span>Passwords match</span>
                </div>
              </div>
            </div>

            {/* Submit Action */}
            <button
              className="btn btn-primary w-100"
              type="submit"
              disabled={!isValid || loading || success}
              style={{ height: "42px", fontWeight: 600 }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving New Password...
                </>
              ) : (
                <>
                  Save New Password
                  <FeatherIcon icon="arrow-right" size={16} className="ms-2" />
                </>
              )}
            </button>

            {/* Logout link */}
            <div className="text-center mt-3">
              <Link
                to="/logout"
                style={{
                  color: "rgba(255, 255, 255, 0.55)",
                  fontSize: "13px",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <FeatherIcon icon="log-out" size={14} />
                <span>Log out and return later</span>
              </Link>
            </div>
          </form>
        </div>
      </AuthLayout>
    </React.Fragment>
  );
};

export default ChangePasswordPage;
